import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { dataService } from '../../services/dataService';
import { HistoryRecord } from '../../types/domain';
import {
  formatDateTime,
  getSiteLabelForHistory
} from '../../lib/domain-data';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { cn } from '../../lib/utils';

export default function Home() {
  const { user, profile } = useAuth();
  const navigate = useNavigate();
  const [lastActivity, setLastActivity] = useState<HistoryRecord | null>(null);

  useEffect(() => {
    if (user) {
      dataService.getHistory(user.id).then(history => {
        if (history.length > 0) {
          setLastActivity(history[0]);
        }
      });
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      dataService.migrateLocalData(user.id);
      dataService.syncOfflineData(user.id);

      const handleOnline = () => {
        dataService.syncOfflineData(user.id);
      };

      window.addEventListener('online', handleOnline);
      return () => {
        window.removeEventListener('online', handleOnline);
      };
    }
    return undefined;
  }, [user]);

  return (
    <div className="screen min-h-[100dvh] h-full">
      <div
        className="screen-card"
        role="group"
        aria-labelledby="mode-step-title"
      >
        <div className="mb-6 pb-4 border-b border-slate-100">
          <div className="text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
            Halo, {profile?.full_name || 'Adik'}! <span className="animate-wave">👋</span>
          </div>

          {lastActivity ? (
            <Card className="mb-6 bg-white/60 backdrop-blur-sm border-indigo-50 shadow-sm">
              <CardHeader className="pb-2 pt-4 px-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-bold text-indigo-500 uppercase tracking-wider flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-indigo-500 animate-pulse"></span>
                    Aktivitas Terakhir
                  </CardTitle>
                  <Badge variant="outline" className="font-normal text-xs text-slate-500 border-indigo-100 bg-white/50">
                    {formatDateTime(lastActivity.date, lastActivity.time)}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="p-4 pt-0 space-y-3">
                <div className="flex justify-between items-start border-b border-indigo-50/50 pb-2">
                  <span className="text-xs text-slate-500 font-medium shrink-0 mt-0.5">
                    {lastActivity.siteId === null ? 'Tindakan' : 'Lokasi'}
                  </span>
                  <span className="text-sm font-semibold text-slate-700 text-right leading-tight max-w-[70%]">
                    {getSiteLabelForHistory(lastActivity.ageGroup, lastActivity.siteId)}
                  </span>
                </div>

                {lastActivity.insulinBrand && (
                  <div className="flex justify-between items-center border-b border-indigo-50/50 pb-2">
                    <span className="text-xs text-slate-500 font-medium">Merk</span>
                    <span className="text-sm font-semibold text-slate-700">
                      {lastActivity.insulinBrand}
                    </span>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3 pt-1">
                  {lastActivity.insulinDose !== undefined && lastActivity.insulinDose !== null && (
                    <div className="bg-blue-50/50 rounded-xl p-3 text-center border border-blue-100/50">
                      <div className="text-[10px] text-blue-400 font-bold uppercase mb-0.5">Dosis</div>
                      <div className="text-lg font-bold text-blue-700">
                        {lastActivity.insulinDose} <span className="text-xs font-medium opacity-70">unit</span>
                      </div>
                    </div>
                  )}

                  {lastActivity.glucoseValue !== undefined && lastActivity.glucoseValue !== null && (
                    <div className={cn(
                      "rounded-xl p-3 text-center border",
                      lastActivity.glucoseValue < 70 ? 'bg-red-50/50 border-red-100/50 text-red-700' :
                        lastActivity.glucoseValue > 250 ? 'bg-orange-50/50 border-orange-100/50 text-orange-700' :
                          'bg-emerald-50/50 border-emerald-100/50 text-emerald-700'
                    )}>
                      <div className="text-[10px] font-bold uppercase mb-0.5 opacity-70">Gula Darah</div>
                      <div className="text-lg font-bold">
                        {lastActivity.glucoseValue} <span className="text-xs font-medium opacity-70">mg/dL</span>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="mb-6 bg-blue-50 border-blue-100">
              <CardContent className="p-4 flex items-center gap-3">
                <span className="text-xl">✨</span>
                <span className="font-medium text-blue-800 text-sm">Belum ada riwayat suntikan. Yuk mulai!</span>
              </CardContent>
            </Card>
          )}
        </div>

        <div id="mode-step-title" className="section-title">
          Mau apa sekarang?
        </div>
        <div className="subtitle">Pilih permainan yang sesuai kegiatan Adik.</div>
        <div className="mode-grid cursor-pointer">
          <button
            type="button"
            className="mode-card hover:border-indigo-500 hover:shadow-md transition-all"
            onClick={() => navigate('/check-glucose')}
          >
            Cek gula darah
          </button>
          <button
            type="button"
            className="mode-card hover:border-indigo-500 hover:shadow-md transition-all"
            onClick={() => navigate('/inject-insulin')}
          >
            Suntik insulin
          </button>
        </div>
      </div>
    </div>
  );
}
